"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Search, FileCode, MapPin, Star, Code as Code2, ChartBar as BarChart3, ArrowRight, Check as CheckCircle2, Sparkles, Calendar } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP, ScrollTrigger);
const processSteps = [{
  number: "01",
  title: "Technical & On-Page Audit",
  phase: "Diagnosis Phase",
  description: "I conduct an exhaustive manual audit of your website structure, crawlability, indexing blockers, site velocity, and content gaps to uncover hidden search bottlenecks.",
  deliverable: "Prioritised Technical Audit & Action Plan",
  icon: "Search"
}, {
  number: "02",
  title: "Strategic On-Page Optimization",
  phase: "Core Foundation",
  description: "I restructure heading hierarchies, refine internal link architecture, optimize meta tags, and align target URLs with explicit user search intent.",
  deliverable: "Complete On-Page Optimization Matrix",
  icon: "FileCode"
}, {
  number: "03",
  title: "Citation & Entity Management",
  phase: "Local & Brand Authority",
  description: "I clean up NAP (Name, Address, Phone) inconsistencies across essential industry directories, building verifiable entity trust for Google and AI engines.",
  deliverable: "Validated Local & Citation Audit",
  icon: "MapPin"
}, {
  number: "04",
  title: "Review Strategy & Social Proof",
  phase: "Trust Acceleration",
  description: "I design sustainable review collection workflows to capture authentic customer feedback, bolstering local rankings and conversions.",
  deliverable: "Custom Review Acquisition Blueprint",
  icon: "Star"
}, {
  number: "05",
  title: "Schema Markup & Entity Data",
  phase: "AI Search Readiness",
  description: "I implement JSON-LD structured data and semantic schema markup so search crawlers and AI answer engines understand your services precisely.",
  deliverable: "Custom Schema Implementation Package",
  icon: "Code2"
}, {
  number: "06",
  title: "Transparent Reporting & ROI",
  phase: "Continuous Growth",
  description: "You receive custom, jargon-free monthly performance reports directly from me, tracking organic rankings, search impressions, lead volume, and ROI.",
  deliverable: "Live Performance Dashboard & Direct Monthly Review",
  icon: "BarChart3"
}];
export default function AuditProcess() {
  const containerRef = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const el = containerRef.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".process-step-card", {
        y: 35
      });
      gsap.to(".process-step-card", {
        opacity: 1,
        y: 0,
        duration: 0.7,
        stagger: 0.12,
        ease: "power2.out",
        scrollTrigger: {
          trigger: el,
          start: "top 80%",
          once: true
        }
      });
      gsap.set(".process-cta-banner", {
        y: 20
      });
      gsap.to(".process-cta-banner", {
        opacity: 1,
        y: 0,
        duration: 0.6,
        ease: "power2.out",
        scrollTrigger: {
          trigger: ".process-cta-banner",
          start: "top 85%",
          once: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set(".process-step-card", {
        opacity: 1,
        y: 0
      });
      gsap.set(".process-cta-banner", {
        opacity: 1,
        y: 0
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: containerRef
  });
  const getStepIcon = (iconName: string) => {
    switch (iconName) {
      case "Search":
        return <Search className="size-5 text-primary" />;
      case "FileCode":
        return <FileCode className="size-5 text-primary" />;
      case "MapPin":
        return <MapPin className="size-5 text-primary" />;
      case "Star":
        return <Star className="size-5 text-primary" />;
      case "Code2":
        return <Code2 className="size-5 text-primary" />;
      case "BarChart3":
        return <BarChart3 className="size-5 text-primary" />;
      default:
        return <Search className="size-5 text-primary" />;
    }
  };
  return <section id="auditoptimizationprocess" data-nav="light" className="bg-background text-foreground py-16 md:py-24 transition-colors">
      <div ref={containerRef} className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
          <Badge variant="secondary" className="px-3 py-1 bg-secondary text-secondary-foreground border border-accent/40 rounded-full font-medium text-xs">
            <Sparkles className="size-3.5 text-accent mr-1" />
            Direct Consultant Engagement
          </Badge>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight font-default">
            My Hands-On SEO &amp; Optimization Process
          </h2>

          <p className="text-muted-foreground text-base sm:text-lg font-default leading-relaxed">
            You work directly with me — no account managers, outsourced execution, or template solutions. 
            I handle every phase of your audit and strategic optimization personally to ensure sustainable organic search growth.
          </p>
        </div>

        <div className="relative">
          <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-accent/30 -translate-y-1/2 z-0" aria-hidden="true" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 relative z-10">
            {processSteps.map((step, i) => <div key={step.number} data-index={i} className="process-step-card flex flex-col h-full" style={{
            opacity: 0
          }}>
                <Card className="h-full border border-border bg-card shadow-sm hover:shadow-md hover:border-primary/40 transition-all rounded-xl overflow-hidden flex flex-col justify-between group">
                  <CardHeader className="p-6 pb-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center justify-center size-9 rounded-lg bg-primary text-primary-foreground font-semibold text-sm shadow-xs font-mono">
                          {step.number}
                        </span>
                        <Badge variant="outline" className="text-xs font-normal border-accent/40 bg-secondary/50 text-foreground">
                          {step.phase}
                        </Badge>
                      </div>

                      <div className="size-10 rounded-full bg-muted flex items-center justify-center group-hover:bg-secondary transition-colors">
                        {getStepIcon(step.icon)}
                      </div>
                    </div>

                    <div className="space-y-1">
                      <CardTitle className="text-xl font-semibold font-default text-foreground group-hover:text-primary transition-colors">
                        {step.title}
                      </CardTitle>
                    </div>

                    <CardDescription className="text-muted-foreground text-sm font-default leading-relaxed">
                      {step.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="p-6 pt-0 mt-auto">
                    <Separator className="my-4 bg-border" />
                    
                    <div className="flex items-start gap-2 bg-muted/60 p-3 rounded-lg border border-border/50">
                      <CheckCircle2 className="size-4 text-accent shrink-0 mt-0.5" />
                      <div>
                        <span className="text-xs font-medium text-foreground block">Deliverable:</span>
                        <span className="text-xs text-muted-foreground font-default">{step.deliverable}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>)}
          </div>
        </div>

        <div className="process-cta-banner mt-16 bg-card border border-border rounded-2xl p-8 md:p-10 shadow-sm relative overflow-hidden" style={{
        opacity: 0
      }}>
          <div className="absolute top-0 right-0 -mr-12 -mt-12 size-48 rounded-full bg-secondary/60 blur-3xl pointer-events-none" />
          <div className="absolute bottom-0 left-0 -ml-12 -mb-12 size-48 rounded-full bg-primary/10 blur-3xl pointer-events-none" />

          <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="space-y-2 text-center lg:text-left max-w-2xl">
              <Badge variant="secondary" className="bg-secondary text-secondary-foreground border border-accent/30 text-xs px-2.5 py-0.5 mb-2">
                Action-Oriented SEO Consultancy
              </Badge>
              <h3 className="text-2xl sm:text-3xl font-semibold font-default text-foreground">
                Ready to elevate your search visibility with a custom audit?
              </h3>
              <p className="text-muted-foreground text-sm sm:text-base font-default">
                Book a free initial consultation directly with me. I will review your domain and outline concrete opportunities for search growth.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-4 shrink-0 w-full sm:w-auto">
              <Button asChild size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 font-medium shadow-xs">
                <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
                  <SafeHydrate><Calendar className="size-4" /></SafeHydrate>
                  <span>Book Free Consultation</span>
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto border-border hover:bg-accent hover:text-accent-foreground font-medium">
                <Link to="/CaseStudiesPage" className="inline-flex items-center justify-center gap-2">
                  <span>Explore Case Studies</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}